import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Box,
  FormLabel,
  MenuItem,
  Select,
  Tooltip
} from '@mui/material';
import { useEffect, useState } from 'react';
import { zipWith, filter } from 'lodash';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { DEFAULT_ADVANCE_BUTTON_STYLE, DEFAULT_CANCEL_BUTTON_STYLE } from '../../../utils/styleHelpers';
import { useSnackbar } from '../../../Context/SnackbarContext';
import { TextBlock } from '../../Atoms/Text';
import { StyledTextArea } from '../../Atoms/StyledTextArea';
import { bulkCreateLinkData, BulkRPMLinkCreateResults } from '../../../Api/Link/LinkApi';
import { Link } from '../../../store/Link';
import { Icon } from '../../Atoms/Icon';
import { ComponentSpinner } from '../../Compounds/Loading/ComponentSpinner';
import { downloadCsvFromData } from '../../../utils/helpers';

const BOX_CLASS = {
  width: '100%',
  display: 'flex',
  flexDirection: 'row',
  margin: '20px 0 20px 0',
  gap: 4,
  padding: '0 25px'
};

const rowCount = 5;

const attrsByStatus = {
  active: { candidate: false, archived: false },
  candidate: { candidate: true, archived: false },
  archived: { candidate: false, archived: true }
};

export interface BulkAddLinksModalParams {
  open: boolean;
  setOpen: (state: boolean) => void;
  changeTab: (index: number, name: string, type: 'main' | 'custom') => void;
}

export const BulkAddLinksModal = ({ open, setOpen, changeTab }: BulkAddLinksModalParams) => {
  const { setSnack } = useSnackbar();
  const [isValidAssetIds, setIsValidAssetIds] = useState<boolean>(false);
  const [isValidRpmAccountIds, setIsValidRpmAccountIds] = useState<boolean>(false);
  const [assetIdsList, setAssetIdsList] = useState<string>('');
  const [rpmAccountIdsList, setRpmAccountIdsList] = useState<string>('');
  const [linkStatus, setLinkStatus] = useState<'active' | 'candidate' | 'archived'>('candidate');
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [step, setStep] = useState<number>(1);
  const [bulkLinkResults, setBulkLinkResults] = useState<BulkRPMLinkCreateResults | null>(null);

  const handleChange = (
    e: React.ChangeEvent<HTMLTextAreaElement>,
    setList: React.Dispatch<React.SetStateAction<string>>,
    setValid: React.Dispatch<React.SetStateAction<boolean>>,
    type: 'uuid' | 'number'
  ) => {
    const text = e.target.value;
    setList(text);

    const invalidDelimiter = /,|;|\t| /g.test(text);
    if (invalidDelimiter) {
      setValid(false);
      setSnack({ message: 'Invalid Delimiter', type: 'error', open: true });
      return;
    }

    const lines = text.split('\n').filter((l) => l.trim() !== '');

    const regex = type === 'number' ? /^\d+$/ : /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

    const invalidId = lines.some((line) => !regex.test(line));

    if (invalidId) {
      setValid(false);
      setSnack({
        message: type === 'number' ? 'IDs must contain only digits' : 'IDs must be valid UUIDs',
        type: 'error',
        open: true
      });
      return;
    }

    if (lines.length === 0) {
      setValid(false);
      setSnack({ message: 'Please enter at least one ID', type: 'error', open: true });
      return;
    }

    setValid(true);
  };

  useEffect(() => {
    if (open) {
      setIsValidAssetIds(false);
      setIsValidRpmAccountIds(false);
      setAssetIdsList('');
      setRpmAccountIdsList('');
      setLinkStatus('candidate');
      setStep(1);
      setIsSubmitting(false);
      setBulkLinkResults(null);
    }
  }, [open]);

  const handleSubmitForm = async () => {
    const assetIds = assetIdsList.split('\n').map((line) => line.trim());
    const rpmAccountIds = rpmAccountIdsList.split('\n').map((line) => {
      const id = line.trim();
      return id ? Number(id) : null;
    });

    const statusField = attrsByStatus[linkStatus];

    const paired = zipWith(assetIds, rpmAccountIds, (assetId, rpmAccountId) => ({
      ...statusField,
      sfdcUpstackId: assetId ?? null,
      rpmAccountId: rpmAccountId ?? null,
      origin: 'Bulk Pair'
    }));

    const newLinks = filter(paired, (item) => item.sfdcUpstackId !== null && item.rpmAccountId !== null);
    setIsSubmitting(true);
    const { data, error } = await bulkCreateLinkData(newLinks as Link[], false);
    if (data) {
      setSnack({ message: 'Your links were created successfully', type: 'success', open: true });
      setIsSubmitting(false);
      setBulkLinkResults(data);
      setStep(2);
    }
    if (error) {
      setSnack({ message: `There was an error creating your links: \n${error.message}`, type: 'error', open: true });
      setIsSubmitting(false);
    }
  };

  const downloadBulkLinkCreateResultsCsv = () => {
    let csvData = 'Asset ID,RPM Account ID,Result\n';
    if (bulkLinkResults) {
      csvData += bulkLinkResults?.success
        .map((link) => `${link.sfdcUpstackId},${link.rpmAccountId},New Link\n`)
        .join('');
      csvData += bulkLinkResults?.duplicate
        .map((link) => `${link.sfdcUpstackId},${link.rpmAccountId},Existing Link Ignored\n`)
        .join('');
      csvData += bulkLinkResults?.missingAsset
        .map((link) => `${link.sfdcUpstackId},${link.rpmAccountId},Asset ID Not Found\n`)
        .join('');
      csvData += bulkLinkResults?.missingRpmAccount
        .map((link) => `${link.sfdcUpstackId},${link.rpmAccountId},RPM Account ID Not Found\n`)
        .join('');
    }
    downloadCsvFromData(csvData, 'BulkLinkCreationResults.csv');
  };

  const statusValues = ['Active', 'Candidate', 'Archived'];

  const handleClose = () => {
    if (isSubmitting) return;
    if (step === 2) {
      const actualIndex = statusValues.findIndex((tab) => tab.toLowerCase() === linkStatus);
      changeTab(actualIndex, statusValues[actualIndex], 'main');
    }
    setOpen(false);
  };

  const maxWidth = step === 1 ? 'md' : 'xs';
  const title = step === 1 ? 'New Links' : 'New Links Results';

  const bulkLinkResultsCount = () => {
    if (!bulkLinkResults) return 0;
    return (
      (bulkLinkResults?.success.length || 0) +
      (bulkLinkResults?.duplicate.length || 0) +
      (bulkLinkResults?.missingRpmAccount.length || 0) +
      (bulkLinkResults?.missingAsset.length || 0)
    );
  };

  return (
    <Dialog
      data-testid="bulk-add-links-modal"
      maxWidth={maxWidth}
      open={open}
      onClose={handleClose}
      fullWidth>
      <DialogTitle>
        <TextBlock
          size="lg"
          color="grey9">
          {title}
        </TextBlock>
      </DialogTitle>
      {step === 1 && (
        <>
          <DialogContent sx={{ minHeight: 70, display: 'flex', flexDirection: 'column' }}>
            <Box sx={BOX_CLASS}>
              <Box sx={{ width: '50%' }}>
                <FormLabel className="!text-sm !text-black mb-2 block">Asset List</FormLabel>
                <StyledTextArea
                  isFocused
                  maxRows={rowCount}
                  disabled={isSubmitting}
                  minRows={rowCount}
                  placeholder="Asset and Provisional Asset UIDs, one per line, maximum 1000."
                  value={assetIdsList}
                  onChange={(e) => handleChange(e, setAssetIdsList, setIsValidAssetIds, 'uuid')}
                />
              </Box>
              <Box sx={{ width: '50%', marginRight: 2 }}>
                <FormLabel className="!text-sm !text-black mb-2 block">RPM Account List</FormLabel>
                <StyledTextArea
                  maxRows={rowCount}
                  minRows={rowCount}
                  disabled={isSubmitting}
                  placeholder="RPM Account IDs, one per line, maximum 1000."
                  value={rpmAccountIdsList}
                  onChange={(e) => handleChange(e, setRpmAccountIdsList, setIsValidRpmAccountIds, 'number')}
                />
              </Box>
            </Box>
            <hr className="my-1" />
            <Box sx={{ width: '100%', padding: '0 25px', marginTop: 2 }}>
              <FormLabel className="!text-sm !text-black mb-2 block">Link Status</FormLabel>
              <Select
                id="edit-link-status-select"
                size="small"
                data-testid="edit-link-status-select"
                value={linkStatus}
                disabled={isSubmitting}
                className="w-auto mr-auto !text-sm"
                onChange={(e) => setLinkStatus(e.target.value)}>
                {statusValues.map((status) => (
                  <MenuItem
                    key={status}
                    value={status.toLowerCase()}
                    className="!text-sm">
                    {status}
                  </MenuItem>
                ))}
              </Select>
            </Box>
          </DialogContent>
          <hr className="mb-1" />
          <DialogActions sx={{ marginRight: 2, pb: 2 }}>
            <button
              className={DEFAULT_CANCEL_BUTTON_STYLE}
              disabled={isSubmitting}
              onClick={handleClose}>
              Cancel
            </button>
            {isSubmitting ? (
              <div className="flex">
                <ComponentSpinner
                  sx={{ mt: '0', m: '0' }}
                  label="Creating..."
                />
              </div>
            ) : (
              <button
                data-testid="submit-new-view"
                disabled={!isValidAssetIds || !isValidRpmAccountIds || isSubmitting}
                onClick={handleSubmitForm}
                className={DEFAULT_ADVANCE_BUTTON_STYLE}
                type="submit">
                Save
              </button>
            )}
          </DialogActions>
        </>
      )}
      {step === 2 && (
        <>
          <DialogContent sx={{ minHeight: 70, display: 'flex', flexDirection: 'column' }}>
            <Box sx={BOX_CLASS}>
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <span>{bulkLinkResultsCount()} ID Pairs</span>
                  <Tooltip
                    title="Download csv"
                    arrow>
                    <button
                      className="text-blue-600 text-sm"
                      onClick={() => downloadBulkLinkCreateResultsCsv()}>
                      <FileDownloadIcon
                        fontSize="small"
                        color="secondary"
                      />
                    </button>
                  </Tooltip>
                </div>
                <div className="flex items-center gap-2">
                  <span style={{ minWidth: '25px' }}>
                    <Icon type="indigoCheckmark" />
                  </span>
                  <span>{bulkLinkResults?.success.length || 0} new links</span>
                </div>
                <div className="flex items-center gap-2">
                  <span style={{ minWidth: '25px' }}>
                    <Icon type="indigoCheckmark" />
                  </span>
                  <span>{bulkLinkResults?.duplicate.length || 0} existing links ignored</span>
                </div>
                <div className="flex items-center gap-2">
                  <span style={{ minWidth: '25px' }}>
                    <Icon type="alert" />
                  </span>
                  <span>
                    {(bulkLinkResults?.missingRpmAccount.length || 0) + (bulkLinkResults?.missingAsset.length || 0)} IDs
                    not found
                  </span>
                </div>
              </div>
            </Box>
          </DialogContent>
          <hr className="mb-1" />
          <DialogActions sx={{ marginRight: 2, pb: 2 }}>
            <button
              className={DEFAULT_CANCEL_BUTTON_STYLE}
              onClick={handleClose}>
              Close
            </button>
          </DialogActions>
        </>
      )}
    </Dialog>
  );
};
